import { useEffect, useState, useContext, useCallback } from "react";
import { localStorage, ViewerContext } from "dash-ui";
import { useGetFullAccountLazyQuery } from "gql/generated.graphql";

const useInitViewer = () => {
  const [isReady, setIsReady] = useState(false);
  const [hasInitialLoadStarted, setHasInitialLoadStarted] = useState(false);
  const {
    tokens,
    setAccount,
    setProfile,
    setTokens,
  } = useContext(ViewerContext);
  const { accessToken, refreshToken } = tokens;

  // Use lazy query for better control over when it executes
  const [getAccount, { loading: isViewerLoading, data: accountData, error }] =
    useGetFullAccountLazyQuery({
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      // Don't let Apollo's error handler trigger logout for this query
      errorPolicy: "all",
    });

  const account = accountData?.getAccount;

  // Execute query when we have valid tokens
  useEffect(() => {
    const currentLocalRefreshToken = localStorage.getRefreshToken();

    // Execute if we have a refresh token (with or without access token)
    if (refreshToken && currentLocalRefreshToken) {
      // Always fetch account when we have a refresh token
      // The token refresh link will handle expired access tokens
      // and the auth link will use the fresh token from localStorage
      setHasInitialLoadStarted(true);
      getAccount();
    } else if (!refreshToken && !currentLocalRefreshToken) {
      // No tokens at all - user is not logged in
      setHasInitialLoadStarted(true);
      setIsReady(true);
    }
  }, [refreshToken, getAccount]);

  useEffect(() => {
    if (!isViewerLoading && hasInitialLoadStarted) {
      // Only set account if refresh token still exists in localStorage
      // This prevents setting account after logout
      const currentRefreshToken = localStorage.getRefreshToken();
      
      if (account && currentRefreshToken && setAccount) {
        setAccount(account);
        if (account?.profile && setProfile) {
          setProfile(account?.profile);
        }
      }
      if (error) {
        // If there's an error and no account, user is likely unauthorized
        // Clear tokens to ensure clean state
        const hasNoAccount = !account;
        if (hasNoAccount && localStorage.getRefreshToken()) {
          // Only clear if we still have a refresh token (not already logged out)
          localStorage.setAccessToken("");
          localStorage.setRefreshToken("");
          localStorage.setRefreshTokenExpiry("");
          // Update context to match localStorage
          if (setTokens) {
            setTokens({
              accessToken: "",
              refreshToken: "",
              refreshTokenExpiry: "",
            });
          }
        }
      }
      setIsReady(true);
    }
  }, [isViewerLoading, hasInitialLoadStarted, setAccount, setProfile, account, error, setTokens]);

  const onLocalStorageChanged = useCallback(() => {
    const newAccessToken = localStorage.getAccessToken() || "";
    const newRefreshToken = localStorage.getRefreshToken() || "";
    const newRefreshTokenExpiry = localStorage.getRefreshTokenExpiry() || "";

    // Update tokens in context
    if (setTokens) {
      setTokens({
        accessToken: newAccessToken,
        refreshToken: newRefreshToken,
        refreshTokenExpiry: newRefreshTokenExpiry,
      });
    }

    // Clear account if tokens are removed (logout)
    if (!newAccessToken && !newRefreshToken && setAccount) {
      setAccount(null);
    }
  }, [setTokens, setAccount]);

  useEffect(() => {
    window.addEventListener("storage", onLocalStorageChanged);
    return () => {
      window.removeEventListener("storage", onLocalStorageChanged);
    };
  }, [onLocalStorageChanged]);
  return [isReady];
};
export default useInitViewer;
